import { QRCode } from '@frontend/icons';
import { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LuX } from 'react-icons/lu';
import { FormattedMessage } from 'react-intl';

import { HUMAN_READABLE, RELEASE_DATE, UNIQUE_SHA, VERSION_NUMBER } from '../../../../version';
import { useKioskNavigate } from '../../../utils/kiosk-navigation';

const ID = 'general-info-modal';
export interface GeneralInfoModalProps {
    show: boolean;
    onClose: () => void;
}

const GeneralInfoModal = (props: GeneralInfoModalProps) => {
    const navigate = useKioskNavigate();
    const [versionPopover, setVersionPopover] = useState(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const startPress = useCallback(() => {
        timerRef.current = setTimeout(() => setVersionPopover(true), 600);
    }, []);

    const cancelPress = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    if (!props.show) {
        return null;
    }

    const root = document.getElementById('root');
    if (!root) return null;

    return createPortal(
        <div
            id={ID}
            className='fixed inset-0 z-1000 flex items-center justify-center'>
            <div
                onClick={props.onClose}
                className='absolute inset-0 bg-stone-950/40'
                data-name='Background'
            />

            <div
                id={ID}
                className='bg-background-surface border-border-default relative z-10 flex w-96 flex-col items-center justify-center gap-6 overflow-hidden rounded-2xl border p-6'>
                <div className='flex flex-col items-start justify-start gap-6 self-stretch'>
                    <div className='inline-flex h-6 items-start justify-between self-stretch'>
                        <div className='text-foreground-default justify-start text-xl font-semibold'>
                            <FormattedMessage
                                defaultMessage='Kiosk info'
                                description='Title for the general info modal, which shows general information about the kiosk and allows users to read the EULA and privacy policy.'
                                id='generalInfoModal.title'
                            />
                        </div>
                        <button
                            className='text-foreground-default disabled:text-brand-disabled relative flex size-6 cursor-pointer items-center justify-center overflow-hidden transition-colors'
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                props.onClose();
                            }}>
                            <LuX className='size-full' />
                        </button>
                    </div>
                </div>
                <div className='flex flex-col items-start justify-start gap-2 self-stretch'>
                    <div
                        className='bg-background-default inline-flex items-center justify-start gap-3 self-stretch rounded-2xl p-3'
                        onClick={() => navigate('info/read-eula')}>
                        <QRCode className='size-6' />
                        <div className='text-always-black justify-end text-base font-medium'>
                            <FormattedMessage
                                defaultMessage='Read EULA'
                                description='Button text for reading the End User License Agreement (EULA) in the general info modal.'
                                id='generalInfoModal.readEula'
                            />
                        </div>
                    </div>
                    <div
                        className='bg-background-default inline-flex items-center justify-start gap-3 self-stretch rounded-2xl p-3'
                        onClick={() => navigate('info/read-privacy-policy')}>
                        <QRCode className='size-6' />
                        <div className='text-always-black justify-end text-base font-medium'>
                            <FormattedMessage
                                defaultMessage='Read privacy policy'
                                description='Button text for reading the privacy policy in the general info modal.'
                                id='generalInfoModal.readPrivacyPolicy'
                            />
                        </div>
                    </div>
                </div>
                <div className='border-border-default flex w-full flex-col items-center border-t pt-4'>
                    <div className='relative'>
                        <div
                            className='py-1 select-none'
                            onPointerDown={startPress}
                            onPointerUp={cancelPress}
                            onPointerLeave={cancelPress}
                            onPointerCancel={cancelPress}>
                            <span className='text-foreground-muted text-xs font-light'>{VERSION_NUMBER}</span>
                        </div>
                        {versionPopover && (
                            <>
                                <div
                                    className='fixed inset-0 z-40'
                                    onPointerDown={() => setVersionPopover(false)}
                                />
                                <div className='bg-background-surface border-border-default absolute bottom-full left-1/2 z-50 mb-2 flex min-w-56 -translate-x-1/2 flex-col gap-2 rounded-2xl border p-4 shadow-lg'>
                                    <p className='text-foreground-default text-xs font-semibold'>
                                        <FormattedMessage
                                            id='generalInfoModal.version.title'
                                            defaultMessage='Version info'
                                            description='Title of the version info popover in kiosk info modal'
                                        />
                                    </p>
                                    <div className='flex flex-col gap-1'>
                                        <VersionRow
                                            label={
                                                <FormattedMessage
                                                    id='generalInfoModal.version.version'
                                                    defaultMessage='Version'
                                                    description='Version label in kiosk info modal'
                                                />
                                            }
                                            value={HUMAN_READABLE}
                                        />
                                        <VersionRow
                                            label={
                                                <FormattedMessage
                                                    id='generalInfoModal.version.number'
                                                    defaultMessage='Number'
                                                    description='Number label in kiosk info modal'
                                                />
                                            }
                                            value={VERSION_NUMBER}
                                        />
                                        <VersionRow
                                            label={
                                                <FormattedMessage
                                                    id='generalInfoModal.version.sha'
                                                    defaultMessage='SHA'
                                                    description='SHA label in kiosk info modal'
                                                />
                                            }
                                            value={UNIQUE_SHA}
                                        />
                                        <VersionRow
                                            label={
                                                <FormattedMessage
                                                    id='generalInfoModal.version.released'
                                                    defaultMessage='Released'
                                                    description='Released label in kiosk info modal'
                                                />
                                            }
                                            value={RELEASE_DATE}
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>,
        root
    );
};

export default GeneralInfoModal;

const VersionRow = ({ label, value }: { label: React.ReactNode; value: string }) => (
    <div className='flex items-center justify-between gap-4'>
        <span className='text-foreground-muted text-xs'>{label}</span>
        <span className='text-foreground-default font-mono text-xs'>{value}</span>
    </div>
);
